// 本地存储工具类
class BlogStorageManager {
  constructor() {
    this.prefix = 'blog_';
    this.keys = {
      categories: 'categories',
      articles: 'articles',
      drafts: 'drafts',
      tags: 'tags',
      settings: 'settings',
      analytics: 'analytics'
    };
    
    // 初始化数据
    this.initData();
  }

  // 初始化数据
  initData() {
    // 初始化分类数据
    if (!this.getCategories().length) {
      this.setCategories([
        {
          id: 'cat_default',
          name: '默认分类',
          description: '未分类的文章',
          parentId: null,
          articleCount: 0,
          order: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ]);
    }

    // 初始化设置数据
    if (!this.getSettings()) {
      this.setSettings({
        blogInfo: {
          title: '我的技术博客',
          description: '分享技术心得和学习笔记',
          author: '博主',
          avatar: ''
        },
        displaySettings: {
          theme: 'dark',
          fontSize: 14,
          layout: 'compact',
          autoSave: 30000
        },
        editorSettings: {
          defaultCategory: 'cat_default',
          editorMode: 'rich',
          autoSaveInterval: 30000
        },
        privacySettings: {
          articleVisibility: 'public',
          allowComments: true,
          accessControl: 'open'
        },
        syncSettings: {
          provider: 'localStorage',
          autoSync: false,
          syncInterval: 30000,
          conflictResolution: 'local-wins',
          offlineMode: true,
          maxRetries: 3
        }
      });
    }

    // 初始化统计数据
    if (!this.getAnalytics()) {
      this.setAnalytics({
        totalArticles: 0,
        totalViews: 0,
        totalLikes: 0,
        totalWords: 0,
        monthlyStats: [],
        categoryStats: [],
        tagStats: []
      });
    }
  }

  // 通用存储方法
  setItem(key, data) {
    try {
      const fullKey = this.prefix + key;
      const jsonData = JSON.stringify({
        data,
        timestamp: new Date().toISOString(),
        version: '1.0.0'
      });
      localStorage.setItem(fullKey, jsonData);
      return true;
    } catch (error) {
      console.error('Storage set error:', error);
      return false;
    }
  }

  // 通用获取方法
  getItem(key) {
    try {
      const fullKey = this.prefix + key;
      const jsonData = localStorage.getItem(fullKey);
      if (!jsonData) return null;
      
      const parsedData = JSON.parse(jsonData);
      return parsedData.data;
    } catch (error) {
      console.error('Storage get error:', error);
      return null;
    }
  }

  // 通用删除方法
  removeItem(key) {
    try {
      const fullKey = this.prefix + key;
      localStorage.removeItem(fullKey);
      return true;
    } catch (error) {
      console.error('Storage remove error:', error);
      return false;
    }
  }

  // 生成唯一ID
  generateId(prefix = 'id') {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // 分类相关方法
  getCategories() {
    return this.getItem(this.keys.categories) || [];
  }

  setCategories(categories) {
    return this.setItem(this.keys.categories, categories);
  }

  addCategory(categoryData) {
    const categories = this.getCategories();
    const newCategory = {
      id: this.generateId('cat'),
      name: categoryData.name,
      description: categoryData.description || '',
      parentId: categoryData.parentId || null,
      articleCount: 0,
      order: categories.length + 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    categories.push(newCategory);
    this.setCategories(categories);
    return newCategory;
  }

  updateCategory(id, updates) {
    const categories = this.getCategories();
    const index = categories.findIndex(cat => cat.id === id);
    
    if (index !== -1) {
      categories[index] = {
        ...categories[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };
      this.setCategories(categories);
      return categories[index];
    }
    return null;
  }

  deleteCategory(id) {
    const categories = this.getCategories();
    const filteredCategories = categories.filter(cat => cat.id !== id);
    
    // 如果删除的分类有文章，将文章移动到默认分类
    const articles = this.getArticles();
    const updatedArticles = articles.map(article => {
      if (article.categoryId === id) {
        return { ...article, categoryId: 'cat_default' };
      }
      return article;
    });
    this.setArticles(updatedArticles);
    
    this.setCategories(filteredCategories);
    return true;
  }

  // 文章相关方法
  getArticles() {
    return this.getItem(this.keys.articles) || [];
  }

  setArticles(articles) {
    return this.setItem(this.keys.articles, articles);
  }

  addArticle(articleData) {
    const articles = this.getArticles();
    const newArticle = {
      id: this.generateId('art'),
      title: articleData.title,
      content: articleData.content,
      categoryId: articleData.categoryId || 'cat_default',
      tags: articleData.tags || [],
      status: 'published',
      viewCount: 0,
      likeCount: 0,
      wordCount: this.countWords(articleData.content),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      publishedAt: new Date().toISOString(),
      isTop: false,
      isFeatured: false
    };
    
    articles.push(newArticle);
    this.setArticles(articles);
    
    // 更新分类文章数量
    this.updateCategoryArticleCount(newArticle.categoryId);
    
    // 更新统计数据
    this.updateAnalytics();
    
    return newArticle;
  }

  updateArticle(id, updates) {
    const articles = this.getArticles();
    const index = articles.findIndex(art => art.id === id);
    
    if (index !== -1) {
      const oldCategoryId = articles[index].categoryId;
      
      articles[index] = {
        ...articles[index],
        ...updates,
        updatedAt: new Date().toISOString(),
        wordCount: updates.content ? this.countWords(updates.content) : articles[index].wordCount
      };
      
      // 如果分类发生变化，更新分类文章数量
      if (updates.categoryId && updates.categoryId !== oldCategoryId) {
        this.updateCategoryArticleCount(oldCategoryId);
        this.updateCategoryArticleCount(updates.categoryId);
      }
      
      this.setArticles(articles);
      this.updateAnalytics();
      
      return articles[index];
    }
    return null;
  }

  deleteArticle(id) {
    const articles = this.getArticles();
    const article = articles.find(art => art.id === id);
    
    if (article) {
      const filteredArticles = articles.filter(art => art.id !== id);
      this.setArticles(filteredArticles);
      
      // 更新分类文章数量
      this.updateCategoryArticleCount(article.categoryId);
      this.updateAnalytics();
      
      return true;
    }
    return false;
  }

  // 草稿相关方法
  getDrafts() {
    return this.getItem(this.keys.drafts) || [];
  }

  setDrafts(drafts) {
    return this.setItem(this.keys.drafts, drafts);
  }

  addDraft(draftData) {
    const drafts = this.getDrafts();
    const newDraft = {
      id: this.generateId('draft'),
      title: draftData.title || '未命名草稿',
      content: draftData.content || '',
      categoryId: draftData.categoryId || 'cat_default',
      tags: draftData.tags || [],
      wordCount: this.countWords(draftData.content || ''),
      lastEditedAt: new Date().toISOString(),
      createdAt: new Date().toISOString()
    };
    
    drafts.push(newDraft);
    this.setDrafts(drafts);
    return newDraft;
  }

  updateDraft(id, updates) {
    const drafts = this.getDrafts();
    const index = drafts.findIndex(draft => draft.id === id);
    
    if (index !== -1) {
      drafts[index] = {
        ...drafts[index],
        ...updates,
        lastEditedAt: new Date().toISOString(),
        wordCount: updates.content ? this.countWords(updates.content) : drafts[index].wordCount
      };
      this.setDrafts(drafts);
      return drafts[index];
    }
    return null;
  }

  deleteDraft(id) {
    const drafts = this.getDrafts();
    const filteredDrafts = drafts.filter(draft => draft.id !== id);
    this.setDrafts(filteredDrafts);
    return true;
  }

  // 将草稿发布为文章
  publishDraft(id) {
    const drafts = this.getDrafts();
    const draft = drafts.find(d => d.id === id);
    
    if (draft) {
      // 创建文章
      const article = this.addArticle({
        title: draft.title,
        content: draft.content,
        categoryId: draft.categoryId,
        tags: draft.tags
      });
      
      // 删除草稿
      this.deleteDraft(id);
      
      return article;
    }
    return null;
  }

  // 标签相关方法
  getTags() {
    return this.getItem(this.keys.tags) || [];
  }

  setTags(tags) {
    return this.setItem(this.keys.tags, tags);
  }

  addTag(tagData) {
    const tags = this.getTags();
    const existingTag = tags.find(tag => tag.name === tagData.name);
    
    if (existingTag) {
      return existingTag;
    }
    
    const newTag = {
      id: this.generateId('tag'),
      name: tagData.name,
      description: tagData.description || '',
      articleCount: 0,
      relatedTags: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    tags.push(newTag);
    this.setTags(tags);
    return newTag;
  }

  updateTag(id, updates) {
    const tags = this.getTags();
    const index = tags.findIndex(tag => tag.id === id);
    
    if (index !== -1) {
      tags[index] = {
        ...tags[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };
      this.setTags(tags);
      return tags[index];
    }
    return null;
  }

  deleteTag(id) {
    const tags = this.getTags();
    const filteredTags = tags.filter(tag => tag.id !== id);
    this.setTags(filteredTags);
    
    // 从所有文章中移除该标签
    const articles = this.getArticles();
    const updatedArticles = articles.map(article => ({
      ...article,
      tags: article.tags.filter(tagName => {
        const tag = tags.find(t => t.id === id);
        return tag ? tagName !== tag.name : true;
      })
    }));
    this.setArticles(updatedArticles);
    
    return true;
  }

  // 设置相关方法
  getSettings() {
    return this.getItem(this.keys.settings);
  }

  setSettings(settings) {
    return this.setItem(this.keys.settings, settings);
  }

  updateSettings(updates) {
    const currentSettings = this.getSettings();
    const newSettings = { ...currentSettings, ...updates };
    this.setSettings(newSettings);
    return newSettings;
  }

  // 统计相关方法
  getAnalytics() {
    return this.getItem(this.keys.analytics);
  }

  setAnalytics(analytics) {
    return this.setItem(this.keys.analytics, analytics);
  }

  updateAnalytics() {
    const articles = this.getArticles();
    const categories = this.getCategories();
    const tags = this.getTags();
    
    // 计算基础统计
    const totalArticles = articles.length;
    const totalViews = articles.reduce((sum, art) => sum + (art.viewCount || 0), 0);
    const totalLikes = articles.reduce((sum, art) => sum + (art.likeCount || 0), 0);
    const totalWords = articles.reduce((sum, art) => sum + (art.wordCount || 0), 0);
    
    // 计算月度统计
    const monthlyStats = this.calculateMonthlyStats(articles);
    
    // 计算分类统计
    const categoryStats = categories.map(category => {
      const categoryArticles = articles.filter(art => art.categoryId === category.id);
      return {
        category: category.name,
        count: categoryArticles.length,
        percentage: totalArticles > 0 ? Math.round((categoryArticles.length / totalArticles) * 100) : 0
      };
    });
    
    // 计算标签统计
    const tagStats = this.calculateTagStats(articles, tags);
    
    const analytics = {
      totalArticles,
      totalViews,
      totalLikes,
      totalWords,
      monthlyStats,
      categoryStats,
      tagStats,
      lastUpdated: new Date().toISOString()
    };
    
    this.setAnalytics(analytics);
    return analytics;
  }

  // 工具方法
  countWords(content) {
    if (!content) return 0;
    // 简单的字数统计，支持中英文
    // 移除markdown标记后计算字数
    const plainText = content
      .replace(/```[\s\S]*?```/g, '') // 移除代码块
      .replace(/`[^`]*`/g, '') // 移除行内代码
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // 移除链接，保留文字
      .replace(/!\[[^\]]*\]\([^)]+\)/g, '') // 移除图片
      .replace(/[#*>`\-_~]/g, '') // 移除markdown标记
      .replace(/\s+/g, ''); // 移除空白字符
    return plainText.length;
  }

  updateCategoryArticleCount(categoryId) {
    const articles = this.getArticles();
    const categories = this.getCategories();
    
    const category = categories.find(cat => cat.id === categoryId);
    if (category) {
      const articleCount = articles.filter(art => art.categoryId === categoryId).length;
      this.updateCategory(categoryId, { articleCount });
    }
  }

  calculateMonthlyStats(articles) {
    const monthlyData = {};
    
    articles.forEach(article => {
      const date = new Date(article.publishedAt || article.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { articles: 0, views: 0 };
      }
      
      monthlyData[monthKey].articles++;
      monthlyData[monthKey].views += article.viewCount || 0;
    });
    
    return Object.entries(monthlyData)
      .map(([month, data]) => ({
        month,
        articles: data.articles,
        views: data.views
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }

  calculateTagStats(articles, tags) {
    const tagCount = {};
    
    articles.forEach(article => {
      article.tags.forEach(tagName => {
        tagCount[tagName] = (tagCount[tagName] || 0) + 1;
      });
    });
    
    return Object.entries(tagCount)
      .map(([tagName, count]) => ({
        tag: tagName,
        count,
        percentage: articles.length > 0 ? Math.round((count / articles.length) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count);
  }

  // 数据备份和恢复
  exportData() {
    return {
      categories: this.getCategories(),
      articles: this.getArticles(),
      drafts: this.getDrafts(),
      tags: this.getTags(),
      settings: this.getSettings(),
      analytics: this.getAnalytics(),
      exportTime: new Date().toISOString(),
      version: '1.0.0'
    };
  }

  importData(data) {
    try {
      if (data.categories) this.setCategories(data.categories);
      if (data.articles) this.setArticles(data.articles);
      if (data.drafts) this.setDrafts(data.drafts);
      if (data.tags) this.setTags(data.tags);
      if (data.settings) this.setSettings(data.settings);
      if (data.analytics) this.setAnalytics(data.analytics);
      
      return true;
    } catch (error) {
      console.error('Import data error:', error);
      return false;
    }
  }

  clearAllData() {
    Object.values(this.keys).forEach(key => {
      this.removeItem(key);
    });
    this.initData();
  }
}

// 创建单例实例
const blogStorage = new BlogStorageManager();

export default blogStorage;
